import React, { useCallback } from 'react';
import { FaeKey, Blessing } from '../../../../types';
import { capitalize } from '../../../../utils/capitalize';
import { getIconPath } from '../../../../utils/iconPath';
import { highlightKeywords } from '../../../helpers/highlightKeywords';

export const getBlessingText = (blessing: Blessing): string => {
    if ('type' in blessing) {
        // Hide effect text for ability blessings
        const abilityTypes = ['Light Attack', 'Heavy Attack', 'Spell', 'Dash', 'Companion', 'Ultimate'];
        if (abilityTypes.includes(blessing.type)) {
            // Only return description if it exists, never the effect
            // If the description is missing, it will return undefined/empty string which is handled by UI
             if ('description' in blessing) return blessing.description;
             return '';
        }
    }
    
    if ('description' in blessing) return blessing.description;
    if ('effect' in blessing) return blessing.effect;
    return '';
};

export const BlessingItem = React.memo(({ 
    blessing, 
    color, 
    isSelected, 
    toggleBlessing, 
    getBlessingTypeDisplayName 
}: {
    blessing: Blessing & { fae: FaeKey };
    color: { hex: string };
    isSelected: boolean;
    toggleBlessing?: (name: string) => void;
    getBlessingTypeDisplayName: (type: string) => string;
}) => {
    const handleClick = useCallback(() => {
        if (toggleBlessing) toggleBlessing(blessing.name);
    }, [toggleBlessing, blessing.name]);

    const blessingType = getBlessingTypeDisplayName(blessing.type);
    const isAbility = blessingType === 'Ability';
    const iconUrl = getIconPath('blessing', blessing.name, { faeLord: blessing.fae, blessingType: blessing.type });

    return (
        <div 
            onClick={handleClick}
            className={`compendium-list-item ${isSelected ? 'selected' : ''}`}
            style={isSelected ? { 
                backgroundColor: `${color.hex}1A`, // 10% opacity (approx)
                borderColor: `${color.hex}4D` // 30% opacity (approx)
            } : {}}
        >
            <img src={iconUrl} alt={blessing.name} className="w-16 h-16 rounded-md border border-slate-600 flex-shrink-0" />
            <div className="flex-grow overflow-hidden">
                <div className="compendium-item-header">
                    <div className="flex items-baseline">
                        <span className={`font-semibold text-sm`} style={{ color: isSelected ? color.hex : color.hex }}>
                            {blessing.name}
                        </span>
                        <span className="text-[10px] font-mono truncate text-sworn-gold" style={{ marginLeft: '6px' }}>
                            ({isAbility ? capitalize(blessing.type) : blessingType}
                            {'partner' in blessing && ` w/ ${capitalize(blessing.partner)}`})
                        </span>
                    </div>
                    {isSelected && (
                        <div className="compendium-selection-badge text-black" style={{
                            backgroundColor: color.hex,
                        }}>
                            Active
                        </div>
                    )}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                    {highlightKeywords(getBlessingText(blessing), blessing.fae)}
                </p>
            </div>
        </div>
    );
});
